import { useLocation } from 'wouter';
import { ChevronDown, Command, LogOut, Menu, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { logout } from '@/lib/auth';
import { getUserInitials, useCurrentUser } from '@/hooks/use-current-user';
import { EvidenceFreshnessBar } from '@/components/allocator/evidence-freshness-bar';
import { ThemeToggle } from '@/components/theme-toggle';

const pageTitles: Record<string, string> = {
  '/': 'Decision',
  '/strategy': 'Strategy',
  '/strategies': 'Strategy',
  '/performance': 'Performance',
  '/risk': 'Performance',
  '/operations': 'Operations',
  '/execution': 'Operations',
  '/connections': 'Operations',
  '/evidence': 'Evidence',
  '/audit': 'Evidence',
  '/trade-ledger': 'Evidence',
  '/testing': 'Evidence',
  '/governance': 'Governance',
  '/approvals': 'Governance',
  '/settings': 'Governance',
};

interface TopbarProps {
  onMobileMenuClick?: () => void;
}

export default function Topbar({ onMobileMenuClick }: TopbarProps) {
  const [location, setLocation] = useLocation();
  const pathname = location.split('?')[0] || '/';
  const title = pageTitles[pathname] || 'Client Workspace';
  const { data: user } = useCurrentUser();
  const initials = getUserInitials(user);
  const displayName = user?.name || 'QuantForge Operator';
  const displayRole = user?.role || 'Read-only operator';

  const openCommandPalette = () => {
    window.dispatchEvent(new Event('qf:open-command-palette'));
  };

  const handleLogout = async (event: React.MouseEvent) => {
    const target = event.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
    target.style.pointerEvents = 'none';
    await logout();
  };

  return (
    <header className="qf-topbar sticky top-0 z-30 border-b">
      <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            aria-label="Open navigation menu"
            className="-ml-2 text-slate-400 hover:text-slate-100 md:hidden"
            onClick={onMobileMenuClick}
            size="icon"
            variant="ghost"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <p className="truncate text-base font-semibold text-white">
            <span data-testid="text-page-title">{title}</span>
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label="Open command palette"
                className="hidden h-10 rounded-md border border-white/10 bg-white/[0.035] px-3 text-slate-300 hover:bg-white/[0.06] hover:text-white md:inline-flex"
                onClick={openCommandPalette}
                type="button"
                variant="ghost"
              >
                <Command className="mr-2 h-4 w-4" />
                <span className="text-[11px] font-semibold tracking-[0.22em] text-slate-500">
                  K
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open command palette</TooltipContent>
          </Tooltip>

          <ThemeToggle />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-10 rounded-md border border-white/10 bg-white/[0.035] px-2.5 text-left hover:bg-white/[0.06]"
                data-testid="button-user-menu"
                variant="ghost"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-[#10181D]">
                    <span className="text-sm font-semibold text-[#7AD8CC]">{initials}</span>
                  </div>
                  <div className="hidden min-w-0 text-left md:block">
                    <p className="truncate text-sm font-medium text-white">{displayName}</p>
                    <p className="truncate text-xs text-slate-500">{displayRole}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 border-white/10 bg-[#10181D] text-slate-200"
            >
              <DropdownMenuItem
                className="focus:bg-white/[0.06] focus:text-white"
                onClick={() => setLocation('/governance')}
              >
                <Scale className="mr-2 h-4 w-4" />
                Governance
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-300 focus:bg-red-500/10 focus:text-red-200"
                data-testid="menu-logout"
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <EvidenceFreshnessBar />
    </header>
  );
}
